import React, { useMemo } from "react";
import {
    TSubscriptionPreferencesContentElementsMap,
    TSubscriptionPreferencesMode,
} from "../subscriptionPreferences/SubscriptionPreferencesModel";
import { IdentificationFormComponent } from "../identificationForm/IdentificationFormComponent";
import { ConfirmationFormComponent } from "../confirmationForm/ConfirmationFormComponent";
import { SubscriptionPreferencesEditFormComponent } from "../editForm/SubscriptionPreferencesEditFormComponent";
import { UmSpErrorContentComponent } from "../error/UmSpErrorContentComponent";
import { useObjectSelector } from "../../../../common/utils/selectors/useSelectorEx";
import { SubscriptionPreferencesLayoutComponent } from "../_shared/layout/SubscriptionPreferencesLayoutComponent";
import { UmSpUserInfoComponent } from "../userInfo/UmSpUserInfoComponent";
import { IState } from "../../../../common/state/IState";
import { UmSpDcFirstNameElement } from "../_shared/dynamicContent/UmSpDcFirstNameElement";
import { Func0, Func1, Func2 } from "redux";
import { nullFn } from "../../../../common/utils/fpUtils";
import { umSpPageDynamicContentSelector } from "../_shared/selectors";
import { ICustomerConfig } from "../_features/loadDynamicContent/LoadDynamicContentModel";

const screensTextsBackUp = {
    identifyScreenTexts: {
        title: "Subscription preferences",
        greetings: "Hi there!",
        logIn: "Please log in to change your preferences.",
        footer:
            "You can adjust your email preferences any time, or instantly opt out of emails of this kind.%%br%%" +
          " Need help with anything? Hit up support.",
    },
    editScreenTexts: {
        greetings: "Hello, %%userName%%!",
        logIn: "Here you can update your subscription preferences.",
        notice:
            "You can be contacted by VikenFiber AS via SMS and email.%%br%%" +
          " It’s highly recommended to have this option turned on for better user experience.",
        generalConsentsTitle: "General consent",
    },
};
const components: TSubscriptionPreferencesContentElementsMap = {
    identify: IdentificationFormComponent,
    confirm: ConfirmationFormComponent,
    edit: SubscriptionPreferencesEditFormComponent,
    saving: SubscriptionPreferencesEditFormComponent,
    saved: SubscriptionPreferencesEditFormComponent,
    error: UmSpErrorContentComponent,
};

const selector = (s: IState) => ({
    mode: s?.userManagement?.subscriptionPreferences?.mode ?? "identify",
});

const allowedModes: TSubscriptionPreferencesMode[] = [
    "edit",
    "saving",
    "saved",
];

function textsSelector(s: IState) {
    let { mode } = selector(s);
    if (allowedModes.includes(mode)) mode = "edit";

    const config =
        umSpPageDynamicContentSelector(s)?.config ?? ({} as ICustomerConfig);
    const identifyScreenTexts = config?.identifyScreenTexts ?? {};

    const screenKey = `${mode}ScreenTexts`;
    return {
        ...screensTextsBackUp.identifyScreenTexts,
        ...identifyScreenTexts,
        ...(screensTextsBackUp[screenKey] ?? {}),
        ...(config[screenKey] ?? {}),
    };
}

export function UmSpFormsScreenComponent() {
    const { mode } = useObjectSelector(selector);
    const content = components[mode];

    const texts = useObjectSelector(textsSelector);
    const textsComponents = useMemo(() => formatTexts(texts), [texts]);

    return (
        <SubscriptionPreferencesLayoutComponent
            texts={textsComponents}
            ContentElement={content}
            UserInfoElement={UmSpUserInfoComponent}
        />
    );
}

function formatTexts<T>(texts: T): T {
    return Object.entries(texts).reduce(
        (s, [k, v]) => ((s[k] = formatTextItem(v)), s),
        {} as T
    );
}

interface IPattern {
    hasPercent: Func0<IPattern>;
    hasSymbol: Func1<string, IPattern>;
    result: Func0<any[]>;
}

const textPattern: Func1<any[], IPattern> = (items = []) => ({
    hasPercent: () => percent1Pattern(items),
    hasSymbol: char => {
        items[items.length - 1] += char;
        return textPattern(items);
    },
    result: () => items,
});

const percent1Pattern: Func1<any[], IPattern> = items => ({
    hasPercent: () => percent2Pattern(items),
    hasSymbol: char => {
        items.push("%" + char);
        return textPattern(items);
    },
    result: () => {
        throw new Error("parse error");
    },
});

const percent2Pattern: Func1<any[], IPattern> = items => ({
    hasPercent: () => {
        items.push("%%");
        return textPattern(items);
    },
    hasSymbol: char => procSymbolPattern(items, char),
    result: () => {
        throw new Error("parse error");
    },
});

const procSymbolPattern: Func2<any[], string, IPattern> = (items, key) => ({
    hasPercent: () => percent3Pattern(items, key),
    hasSymbol: char => procSymbolPattern(items, key + char),
    result: () => {
        throw new Error("parse error");
    },
});

const percent3Pattern: Func2<any[], string, IPattern> = (items, key) => ({
    hasPercent: () => {
        items.push({ key }, "");
        return textPattern(items);
    },
    hasSymbol: () => {
        throw new Error("parse error");
    },
    result: () => {
        items.push({ key });
        return items;
    },
});

const componentsMap = {
    username: UmSpDcFirstNameElement,
    br: () => <br />,
};

function formatTextItem(text: string) {
    let monad = textPattern([""]);
    for (let char of text)
        monad = char == "%" ? monad.hasPercent() : monad.hasSymbol(char);

    return monad.result().map((item, key) => {
        if (typeof item == "string") return <span key={key}>{item}</span>;
        const Component = componentsMap[item.key.toLowerCase()] ?? nullFn;
        return <Component key={key} />;
    });
}
